import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ApiError, type ApiResponse } from '@/types/api';

/**
 * Shared Axios instance configured to:
 *  - Send cookies on every request (for PHP session auth)
 *  - Mark requests as XHR so PHP recognizes them as AJAX
 *  - Unwrap the ApiResponse envelope into just `data`
 *  - Throw ApiError when the envelope reports {success: false}
 *  - Emit an 'unauthorized' event on 401 so the app can redirect to login
 */

type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

function createClient(): AxiosInstance {
  const client = axios.create({
    // Same origin in prod; dev server proxies API paths to PHP
    baseURL: '/',
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse<unknown>;
      // Non-envelope responses (e.g. static HTML) are passed through untouched.
      if (!body || typeof body !== 'object' || !('success' in body)) {
        return response;
      }
      if (body.success === false) {
        throw new ApiError(body.msg || 'API request failed', body);
      }
      // Replace response.data with just the unwrapped payload to make
      // callers shorter: `const faqs = await api.post('/FaqApi/listAllFaqs')`
      response.data = body.data;
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        for (const handler of unauthorizedHandlers) handler();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const api = createClient();

/**
 * PHP's legacy /request/ endpoints expect POST with form-encoded bodies.
 * The "action" is part of the URL: /request/<apiName>/<actionName>.
 */
export async function callApi<TData = unknown>(
  apiName: string,
  action: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = `/request/${apiName}/${action}`;
  const form = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    form.append(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
  const response = await api.post<TData>(url, form, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(config?.headers || {}),
    },
  });
  return response.data as TData;
}

/**
 * Newer /Dashboard/* API endpoints accept JSON bodies. Use this variant
 * when the backend uses the modern MVC controllers.
 */
export async function callDashboardApi<TData = unknown>(
  path: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = path.startsWith('/') ? path : `/${path}`;
  const response = await api.post<TData>(url, params, config);
  return response.data as TData;
}

/**
 * MVC-style controllers (`/ProductBuilderApi/...`, `/LmsProductApi/...`,
 * `/FaqApi/...` etc.) populate `$this->params` from `$_POST` only — they do
 * NOT read JSON request bodies. The controller framework supports a single
 * `params` form field carrying the full JSON payload, which keeps nested
 * objects, numbers, and booleans typed correctly. Use this helper for those
 * controllers; reserve plain `api.post(...)` for endpoints that explicitly
 * accept JSON bodies.
 */
export async function callMvcApi<TData = unknown>(
  path: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<TData> {
  const url = path.startsWith('/') ? path : `/${path}`;
  const form = new URLSearchParams();
  form.append('params', JSON.stringify(params));
  const response = await api.post<TData>(url, form, {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(config?.headers || {}),
    },
  });
  return response.data as TData;
}
